import { useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

//import { GitHubLogoIcon } from "@radix-ui/react-icons";
import { buttonVariants } from "./ui/button";
import { Menu, LogOut, User, ChevronDown, ChevronRight } from "lucide-react";
import { StaticDarkModeIcon } from "./mode-toggle";
import { LogoIcon } from "./Icons";
import { useAuth } from "../hooks/useAuth";

interface RouteProps {
  href: string;
  label: string;
}

// Keep the old routeList for mobile navigation compatibility
const routeList: RouteProps[] = [
  {
    href: "/features",
    label: "Features",
  },
  {
    href: "#pricing",
    label: "Pricing",
  },
  {
    href: "/news",
    label: "News",
  },
  {
    href: "/about",
    label: "About",
  },
  {
    href: "https://discord.gg/bjfUUFrz",
    label: "Discord",
  },
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [learnDropdownOpen, setLearnDropdownOpen] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  
  // Timeout refs for delayed closing
  const learnTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const aboutTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const location = useLocation();
  const isCheckoutPage = location.pathname === '/checkout';
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  // Dropdown handlers with delay
  const handleLearnMouseEnter = () => {
    if (learnTimeoutRef.current) {
      clearTimeout(learnTimeoutRef.current);
    }
    setLearnDropdownOpen(true);
  };

  const handleLearnMouseLeave = () => {
    learnTimeoutRef.current = setTimeout(() => {
      setLearnDropdownOpen(false);
    }, 200);
  };

  const handleAboutMouseEnter = () => {
    if (aboutTimeoutRef.current) {
      clearTimeout(aboutTimeoutRef.current);
    }
    setAboutDropdownOpen(true);
  };

  const handleAboutMouseLeave = () => {
    aboutTimeoutRef.current = setTimeout(() => {
      setAboutDropdownOpen(false);
    }, 200);
  };

  // Handles scrolling to section from any page
  const handleSectionNav = (sectionId: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname !== "/") {
      navigate(`/?scrollTo=${sectionId}`);
    } else {
      const el = document.getElementById(sectionId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }
    setIsOpen(false);
  };
  
  const getDisplayEmail = (email: string) => {
    if (email.length <= 24) return email;
    return email.slice(0, 24) + '...';
  };

  return (
    <header className="sticky border-b-[1px] top-0 z-40 w-full bg-white dark:border-b-slate-700 dark:bg-background">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-center h-14">
          {/* Logo */}
          <Link
            to="/"
            onClick={e => {
              if (location.pathname === "/") {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
          >
            <LogoIcon />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {/* Features */}
            <Link
              to="/features"
              className={`text-[17px] ${buttonVariants({ variant: "ghost" })}`}
            >
              Features
            </Link>

            {/* Pricing Scroll-to 
            <a
              href="/pricing"
              onClick={handleSectionNav('pricing')}
              className={`text-[17px] ${buttonVariants({ variant: "ghost" })}`}
            >
              Pricing
            </a>
            */}
            
            <Link
              to="/pricing"
              className={`text-[17px] ${buttonVariants({ variant: "ghost" })}`}
            >
              Pricing
            </Link>

            <Link
              to="/news"
              className={`text-[17px] ${buttonVariants({ variant: "ghost" })}`}
            >
              News
            </Link>

            {/* Downloads */}
            <Link
              to="/downloads"
              className={`text-[17px] ${buttonVariants({ variant: "ghost" })}`}
            >
              Download
            </Link>

            <Link to="/support"
              className={`text-[17px] ${buttonVariants({ variant: "ghost" })}`}
            >
              Support
            </Link>

            {/* Learn Dropdown (now with Doyen / Drasil submenus) */}
            <div className="relative">
              <button
                onMouseEnter={handleLearnMouseEnter}
                onMouseLeave={handleLearnMouseLeave}
                className={`text-[17px] flex items-center ${buttonVariants({ variant: "ghost" })}`}
              >
                Learn
                <ChevronDown className="ml-1 h-3 w-3" />
              </button>
              {learnDropdownOpen && (
                <div
                  onMouseEnter={handleLearnMouseEnter}
                  onMouseLeave={handleLearnMouseLeave}
                  className="absolute top-full left-0 mt-1 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50"
                >
                  {/* Doyen subgroup */}
                  <div className="group relative">
                    <button className="w-full text-left px-4 py-2 text-sm flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                      Doyen
                      <ChevronRight className="ml-2 h-3 w-3" />
                    </button>
                    <div className="absolute left-full top-0 ml-1 hidden group-hover:block w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50">
                      <Link to="/setup" className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">Setup</Link>
                      <Link to="/manual" className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">Manual</Link>
                      <a href="https://github.com/1130-Lab/Doyen.gRPC" target="_blank" rel="noopener noreferrer" className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">API Reference</a>
                    </div>
                  </div>

                  {/* Drasil subgroup */}
                  <div className="group relative">
                    <button className="w-full text-left px-4 py-2 text-sm flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                      Drasil (Algo Server)
                      <ChevronRight className="ml-2 h-3 w-3" />
                    </button>
                    <div className="absolute left-full top-0 ml-1 hidden group-hover:block w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50">
                      <Link to="/algorithm-server" className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">Setup</Link>
                      <Link to="/direct-to-venue" className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">Direct-to-Venue</Link>
                      <a href="https://github.com/1130-Lab/Drasil.Venue.Protocol" target="_blank" rel="noopener noreferrer" className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">API Reference</a>
                    </div>
                  </div>

                  {/* Misc links */}
                  <a
                    href="https://discord.gg/ZCg7PMpsSe"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Discord"
                    className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    Discord
                  </a>
                  <a
                    href="#faq"
                    onClick={handleSectionNav('faq')}
                    className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    FAQ
                  </a>
                </div>
              )}
            </div> 

            {/* About Dropdown */}
            <div className="relative">
              <button
                onMouseEnter={handleAboutMouseEnter}
                onMouseLeave={handleAboutMouseLeave}
                className={`text-[17px] flex items-center ${buttonVariants({ variant: "ghost" })}`}
              >
                About
                <ChevronDown className="ml-1 h-3 w-3" />
              </button>
              {aboutDropdownOpen && (
                <div
                  onMouseEnter={handleAboutMouseEnter}
                  onMouseLeave={handleAboutMouseLeave}
                  className="absolute top-full left-0 mt-1 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50"
                >
                  <Link
                    to="/about"
                    className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    Company
                  </Link>
                  <Link
                    to="/roadmap"
                    className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    Roadmap
                  </Link>
                </div>
              )}
            </div>
          </nav>

          {/* Right side - Auth & Mobile */}
          <div className="flex items-center gap-2">
            {/* Desktop Auth */}
            <div className="hidden md:flex gap-2">
              {isAuthenticated ? (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2 px-3 py-2 text-sm bg-muted rounded-md">
                    <User className="h-4 w-4" />
                    <Link to="/profile" className="hover:underline text-primary">
                      {user && getDisplayEmail(user.email)}
                    </Link>
                  </div>
                  <button
                    onClick={logout}
                    className={`border ${buttonVariants({ variant: "outline" })}`}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </button>
                </div>
              ) : (
                <Link
                  to="/login"
                  className={`border ${buttonVariants({ variant: "outline" })}`}
                >
                  Login
                </Link>
              )}
              <a
                href="https://discord.gg/ZCg7PMpsSe"
                target="_blank"
                rel="noopener noreferrer"
                title="Join the Discord"
                className="block py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <img src="/Discord-Symbol-White.png" alt="Join the Discord" style={{ verticalAlign: "middle" }} width={24} height={24}/>
              </a>
              <StaticDarkModeIcon />
            </div>

            {/* Mobile */}
            <span className="flex md:hidden">
              <StaticDarkModeIcon />

              <Sheet
                open={isOpen}
                onOpenChange={setIsOpen}
              >
                <SheetTrigger className="px-2">
                  <Menu
                    className="flex md:hidden h-5 w-5"
                    onClick={() => setIsOpen(true)}
                  >
                    <span className="sr-only">Menu Icon</span>
                  </Menu>
                </SheetTrigger>

                <SheetContent side={"left"}>                
                  <SheetHeader className="flex items-center gap-2">
                    <SheetTitle>
                      <LogoIcon />
                    </SheetTitle>
                  </SheetHeader>                
                  <div className="px-4 text-sm font-medium text-muted-foreground">General</div>
                  <hr className="my-2 w-full mt-3" />
                  <nav className="flex flex-col justify-center items-center gap-2 mt-4">
                    {!isCheckoutPage && routeList.map(({ href, label }: RouteProps) => (
                      href.startsWith('#') ? (
                        <a
                          rel="noreferrer noopener"
                          key={label}
                          href={href}
                          onClick={handleSectionNav(href.replace('#', ''))}
                          className={buttonVariants({ variant: "ghost" })}
                        >
                          {label}
                        </a>
                      ) : href.startsWith('http') ? (
                        <a
                          rel="noreferrer noopener"
                          key={label}
                          href={href}
                          target="_blank"
                          onClick={() => setIsOpen(false)}
                          className={buttonVariants({ variant: "ghost" })}
                        >
                          {label}
                        </a>
                      ) : (
                        <Link
                          key={label}
                          to={href}
                          onClick={() => setIsOpen(false)}
                          className={buttonVariants({ variant: "ghost" })}
                        >
                          {label}
                        </Link>
                      )
                    ))}

                    {isAuthenticated ? (
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-2 px-3 py-2 text-sm bg-muted rounded-md">
                            <User className="h-4 w-4" />
                            <Link
                              to="/profile"
                              className="hover:underline text-primary"
                              onClick={() => setIsOpen(false)}
                            >
                              {user && getDisplayEmail(user.email)}
                            </Link>
                          </div>
                        <button
                          onClick={() => {
                            logout();
                            setIsOpen(false);
                          }}
                          className={`w-[110px] border ${buttonVariants({
                            variant: "outline",
                          })}`}
                        >
                          <LogOut className="mr-2 h-4 w-4" />
                          Logout
                        </button>
                      </div>
                    ) : (
                      <Link
                        rel="noreferrer noopener"
                        to="/login"
                        onClick={() => setIsOpen(false)}
                        className={`w-[110px] border ${buttonVariants({
                          variant: "outline",
                        })}`}
                      >
                        Login
                      </Link>
                    )}

                    {/* Mobile grouped submenu for Doyen / Drasil */}
                    <div className="w-full pt-3">
                      <div className="px-4 text-sm font-medium text-muted-foreground">Doyen</div>
                      <hr className="my-2 w-full mt-3" />
                      <Link to="/setup" onClick={() => setIsOpen(false)} className={`${buttonVariants({ variant: 'ghost' })} w-full text-left`}>Setup</Link>
                      <Link to="/manual" onClick={() => setIsOpen(false)} className={`${buttonVariants({ variant: 'ghost' })} w-full text-left`}>Manual</Link>
                      <a href="https://github.com/1130-Lab/Doyen.gRPC" target="_blank" rel="noopener noreferrer" onClick={() => setIsOpen(false)} className={`${buttonVariants({ variant: 'ghost' })} w-full text-left`}>API Reference</a>
                      <div className="px-4 text-sm font-medium text-muted-foreground mt-3">Drasil</div>
                      <hr className="my-2 w-full mt-3" />
                      <Link to="/algorithm-server" onClick={() => setIsOpen(false)} className={`${buttonVariants({ variant: 'ghost' })} w-full text-left`}>Setup</Link>
                      <Link to="/direct-to-venue" onClick={() => setIsOpen(false)} className={`${buttonVariants({ variant: 'ghost' })} w-full text-left`}>Direct-to-Venue</Link>
                      <a href="https://github.com/1130-Lab/Drasil.Venue.Protocol" target="_blank" rel="noopener noreferrer" onClick={() => setIsOpen(false)} className={`${buttonVariants({ variant: 'ghost' })} w-full text-left`}>API Reference</a>
                    </div>
                  </nav>
                </SheetContent>
              </Sheet>
            </span>
          </div>
        </div>
      </div>
    </header>
  );
};
